import { useCallback, useRef, useState } from 'react';
import { useApolloClient } from '@apollo/client';
import {
    ProductionPlanRatesDocument,
    ProductionPlanRatesQuery,
    ProductionPlanRatesQueryVariables,
} from '../../../services';
import { HOURLY_DATA_EPOCH } from './window';

/**
 * Batched machine / machine x product throughput and waste aggregates.
 *
 * Lives in `smart` rather than beside the planning board that first needed it,
 * because three consumers now share it — the planning board, the Producción
 * list's flags, and the upsert form's Rendimiento tab — and one of those is a
 * `smart` form. A form reaching into `pages` would be the only such import in
 * the codebase and would couple a generic form to a specific page tree.
 *
 * It must stay a SINGLE module backed by a SINGLE GraphQL document: the cache
 * is `cache-first` and keyed by machine:product:fromDate, so a second copy would
 * fragment it and re-fetch the same aggregates per surface.
 *
 * The aggregate now covers ONE window — every run since `fromDate`, which
 * defaults to HOURLY_DATA_EPOCH. The rolling 12-month "recent" window it used to
 * carry alongside was dropped 2026-08-11 (both covered identical runs while
 * hourly data starts at the epoch). `fromDate` is a parameter because the form's
 * Rendimiento tab lets the user move its "Desde"; the planning board and the
 * list omit it and get the epoch. It is part of the cache key: two windows must
 * not share an entry, or the tab would read the list's epoch numbers.
 */
export type RateAggregate = NonNullable<
    ProductionPlanRatesQuery['getMachineProductRates']
>[number];

export interface RatePair {
    machineId: number;
    productId: number | null;
}

// A null product means the machine-level rate; a product id means the machine
// x product rate. `fromDate` joins the key so a moved "Desde" cannot collide
// with the default-epoch entries the list and planning board cache.
// `singleProductOnly` joins it too: the Rendimiento tab's single-product
// aggregates must not collide with the full aggregates every other surface uses.
const cacheKey = (
    machineId: number,
    productId: number | null,
    fromDate: string,
    singleProductOnly: boolean,
): string =>
    `${machineId}:${productId ?? ''}:${fromDate}:${singleProductOnly ? 1 : 0}`;

const emptyRate = (pair: RatePair): RateAggregate => ({
    machine_id: pair.machineId,
    product_id: pair.productId,
    all_kilos: 0,
    all_hours: 0,
    all_runs: 0,
    all_waste: 0,
    all_groups: 0,
    // No history → no rate distribution. Null mean/std with a zero count reads as
    // "not enough runs to grade", which is exactly what a missing pair is.
    all_groups_rate_mean: null,
    all_groups_rate_std: null,
    all_groups_rate_n: 0,
    all_kilos_rate_mean: null,
    all_kilos_rate_std: null,
    all_kilos_rate_n: 0,
});

// Fetch every missing machine and machine x product rate in one GraphQL
// request, then cache each returned pair independently. `ensure` is stable so
// the page effect can depend on it without re-running for every cache update.
// Both `ensure` and `get` take the window's `fromDate`; it defaults to
// HOURLY_DATA_EPOCH so the list and planning board need not pass one.
export function useRatesCache() {
    const client = useApolloClient();
    const [cache, setCache] = useState<Record<string, RateAggregate>>({});
    const cacheRef = useRef<Record<string, RateAggregate>>({});
    const inFlight = useRef<Set<string>>(new Set());

    const ensure = useCallback(
        (
            pairs: RatePair[],
            fromDate: string = HOURLY_DATA_EPOCH,
            singleProductOnly = false,
        ) => {
            const keyFor = (machineId: number, productId: number | null) =>
                cacheKey(machineId, productId, fromDate, singleProductOnly);
            const uniquePairs = Array.from(
                new Map(
                    pairs
                        .filter(
                            (pair) =>
                                Number.isInteger(pair.machineId) &&
                                pair.machineId > 0,
                        )
                        .map((pair) => [
                            keyFor(pair.machineId, pair.productId),
                            pair,
                        ] as const),
                ).values(),
            );
            const missingPairs = uniquePairs.filter((pair) => {
                const key = keyFor(pair.machineId, pair.productId);
                return !cacheRef.current[key] && !inFlight.current.has(key);
            });
            if (missingPairs.length === 0) return;

            missingPairs.forEach((pair) =>
                inFlight.current.add(
                    keyFor(pair.machineId, pair.productId),
                ),
            );
            void client
                .query<
                    ProductionPlanRatesQuery,
                    ProductionPlanRatesQueryVariables
                >({
                    query: ProductionPlanRatesDocument,
                    variables: {
                        pairs: missingPairs.map((pair) => ({
                            machineId: pair.machineId,
                            productId: pair.productId,
                        })),
                        // Pre-hour-capture corridas have hours = 0 and would
                        // distort the kg/hr numerator/denominator; the epoch is
                        // the floor, a moved "Desde" narrows further.
                        fromDate,
                        singleProductOnly,
                    },
                    fetchPolicy: 'cache-first',
                })
                .then((res) => {
                    const returned = res.data?.getMachineProductRates || [];
                    const returnedKeys = new Set(
                        returned.map((rate) =>
                            keyFor(rate.machine_id, rate.product_id ?? null),
                        ),
                    );
                    setCache((current) => {
                        const next = { ...current };
                        returned.forEach((rate) => {
                            next[
                                keyFor(rate.machine_id, rate.product_id ?? null)
                            ] = rate;
                        });
                        // An absent aggregate is a real "no history" answer,
                        // not a still-loading state. Cache zeroes so the UI can
                        // show the no-history state and does not retry the same
                        // empty pair on every render.
                        missingPairs.forEach((pair) => {
                            const key = keyFor(pair.machineId, pair.productId);
                            if (!returnedKeys.has(key)) {
                                next[key] = emptyRate(pair);
                            }
                        });
                        cacheRef.current = next;
                        return next;
                    });
                })
                .catch(() => {
                    // A failed prediction is non-fatal; leave the keys
                    // uncached so a later render can retry.
                })
                .finally(() => {
                    missingPairs.forEach((pair) =>
                        inFlight.current.delete(
                            keyFor(pair.machineId, pair.productId),
                        ),
                    );
                });
        },
        [client],
    );

    const get = useCallback(
        (
            machineId: number,
            productId: number | null,
            fromDate: string = HOURLY_DATA_EPOCH,
            singleProductOnly = false,
        ): RateAggregate | undefined =>
            cache[cacheKey(machineId, productId, fromDate, singleProductOnly)],
        [cache],
    );

    return { ensure, get };
}
